import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AppService } from '../../app.service';
import { ApiRequestService } from '../api-request.service';
import { UtilsService } from '../utils.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { tap } from 'rxjs/operators';
import { ApiDataSource } from '../../utils/api-data-source';
import { RBACService } from '../rbac.service';

@Component({
  selector: 'app-vendors-selector',
  templateUrl: './vendors-selector.component.html',
  styleUrls: ['./vendors-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VendorsSelectorComponent implements OnInit, AfterViewInit {
  // columns to show and the data source
  displayedColumns: string[] = [
    'select',
    'id',
    'name',
    'is_prequalified',
    'contact_details',
    'linked_assets_count',
    'date_created'
  ];
  dataSource: VendorsSelectorDataSource;
  returnAllSelected = false;

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private rbac: RBACService,
    public dialogRef: MatDialogRef<VendorsSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // set multiple if it is not present to false
    if (typeof this.data['multiple'] === 'undefined') {
      this.data['multiple'] = false;
    }

    // set required if it is not present to false
    if (typeof this.data['required'] === 'undefined') {
      this.data['required'] = false;
    }

    // construct the datasource
    this.dataSource = new VendorsSelectorDataSource(
      this.app,
      this.api,
      this.data['multiple']
    );

    // set a default selection of nothing if no selected values are passed through
    if (typeof this.data['selected'] === 'undefined') {
      this.data['selected'] = [];
    }

    // loop and select the values
    for (let i = 0; i < this.data['selected'].length; i++) {
      if (this.data['selected'][i]) {
        this.dataSource.selection.select(this.data['selected'][i]);
      }
    }

    this.returnAllSelected = this.data.returnAllSelected;
  }

  ngOnInit() {
    // Check RBAC permission: standalone vendors:view OR a parent feature that needs vendor
    // selection. Evaluated with exact: false, so any one of these is enough.
    //
    // The asset create/edit dialog opens this selector as a form field and the asset list filter
    // opens it as a filter, so a user who may manage assets but not vendors must still be able to
    // pick one, otherwise the asset form becomes unusable.
    //
    // MUST stay in sync with VendorsController::index().
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_VENDORS,
            RBACService.RBAC_A.A_VIEW
          ),
          // Assets: vendor selection on the create/edit form, and a list filter.
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_ASSETS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_ASSETS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_ASSETS,
            RBACService.RBAC_A.A_EDIT
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_ASSETS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_ASSETS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_ASSETS,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        false
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view vendors.'
      );
      this.dialogRef.close();
      return;
    }

    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order_by = this.sort.active;
      this.dataSource.order = this.sort.direction;

      // Sort UTC by its corresponding date column.
      if (this.sort.active == 'date_created_UTC') {
        this.dataSource.order_by = 'date_created';
      }

      this.dataSource.getData(false);
    });

    // Subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Clears the search bar and performs a search.
   *
   * @returns {void}
   */
  onClearSearch(): void {
    // clear the search field in the datasource
    this.dataSource.search = '';
    // search for records
    this.dataSource.getData(true);
  }

  /**
   * Clear the current selection.
   *
   * @returns {void}
   */
  onClearSelected(): void {
    this.dataSource.selection.clear();
  }

  /**
   * Save the selected data and close the dialog.
   *
   * @return {void}
   */
  onSaveAndClose(): void {
    if (this.returnAllSelected) {
      return this.dialogRef.close({
        selected: this.dataSource.selection.selected,
        isAllSelected: this.dataSource.isAllSelected()
      });
    }

    let response: number | number[];

    if (this.data['multiple']) {
      response = this.dataSource.selection.selected;
    } else {
      if (this.dataSource.selection.selected.length) {
        response = this.dataSource.selection.selected[0];
      } else {
        response = 0;
      }
    }

    this.dialogRef.close(response);
  }

  /**
   * Closes the dialog.
   *
   * @return {void}
   */
  onClose(): void {
    this.dialogRef.close();
  }
}

export class VendorsSelectorDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';
  archived = 'false';

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/vendors', resetOffset, {
      archived: this.archived
    });
  }
}
